import { requireNonNullable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { clamp } from '@disy/lib/math';
import { px } from '@disy/lib/px';
import { tearDown } from '@disy/lib/teardown';
import { throttle } from '@disy/lib/throttle';

import type { ResizeInfo } from 'cadenza/workbook/layout-management/resize-utils';
import { MARGIN, getPosition, getViewPortWithoutScrollSize } from 'cadenza/workbook/selection-cart/layout/layout-utils';
import type { SelectionCart } from 'cadenza/workbook/selection-cart/selection-cart';

// only bottom right corner is supported
export function getSelectionCartResizeCursor({ top, right, bottom, left }: ResizeInfo) {
  if ((bottom && right) || (top && left)) {
    return 'nwse-resize';
  } else if ((bottom && left) || (top && right)) {
    return 'nesw-resize';
  } else if (top || bottom) {
    return 'ns-resize';
  } else if (left || right) {
    return 'ew-resize';
  }
  return '';
}

export function handleSelectionCartResizing(
  pointerDownEvent: PointerEvent,
  selectionCart: SelectionCart & { dResize?: ResizeInfo },
  relativeContainer: HTMLElement,
) {
  relativeContainer.classList.add('disable-user-selection');
  selectionCart.classList.add('is-resizing');
  const styleMap = getComputedStyle(selectionCart);
  const selectionCartRect = selectionCart.getBoundingClientRect();

  const [viewPortWidth, viewPortHeight] = getViewPortWithoutScrollSize();
  // Maximum resizing bounds that considers the viewPort's bound, and also the min/max width and min height of the selection-cart
  const maxTop = selectionCartRect.bottom - parseInt(styleMap.minHeight) - MARGIN;
  const minTop = 0;
  const maxRight = viewPortWidth - selectionCartRect.left - parseInt(styleMap.minWidth) - MARGIN;
  const minRight = Math.max(0, viewPortWidth - selectionCartRect.left - parseInt(styleMap.maxWidth) - MARGIN);
  const maxBottom = viewPortHeight - selectionCartRect.top - parseInt(styleMap.minHeight) - MARGIN;
  const minBottom = 0;
  const maxLeft = selectionCartRect.right - parseInt(styleMap.minWidth) - MARGIN;
  const minLeft = Math.max(0, selectionCartRect.right - parseInt(styleMap.maxWidth) - MARGIN);

  // fixed position from view port
  const position = getPosition(selectionCartRect, new DOMRect(0, 0, viewPortWidth, viewPortHeight));
  Object.assign(selectionCart.style, {
    // Remove width and height when starting resize. It will be set after resize is done
    width: 'unset',
    height: 'unset',
  });
  const resize = requireNonNullable(selectionCart.dResize);
  const { clientX: x, clientY: y } = pointerDownEvent;
  const eventKeys = [
    on(
      window,
      'pointermove',
      throttle((event) => {
        const { clientX: newX, clientY: newY } = event;
        const dx = newX - x;
        const dy = newY - y;

        const resizeTop = () => {
          selectionCart.style.top = px(clamp(position.top + dy, minTop, maxTop));
        };
        const resizeRight = () => {
          selectionCart.style.right = px(clamp(position.right - dx, minRight, maxRight));
        };
        const resizeBottom = () => {
          selectionCart.style.bottom = px(clamp(position.bottom - dy, minBottom, maxBottom));
        };
        const resizeLeft = () => {
          selectionCart.style.left = px(clamp(position.left + dx, minLeft, maxLeft));
        };

        /*
         * Special case is resizing views from corners. We have movement in 2 directions in this case.
         * We calculate the delta x (dx) and delta y (dy) from pointerdown coordinates to pointermove coordinates.
         * This way the resizing view will extend as much as possible in direction of dx or dy
         * depending on which one is higher.
         */
        const bottomRight = resize.bottom && resize.right;
        const topLeft = resize.top && resize.left;
        const bottomLeft = resize.bottom && resize.left;
        const topRight = resize.top && resize.right;
        if (bottomRight || bottomLeft || topRight || topLeft) {
          const xIsMain = dx ** 2 > dy ** 2;
          let resizeMain: () => void;
          let resizeSecondary: () => void;
          if (bottomRight) {
            resizeMain = xIsMain ? resizeRight : resizeBottom;
            resizeSecondary = xIsMain ? resizeBottom : resizeRight;
          } else if (bottomLeft) {
            resizeMain = xIsMain ? resizeLeft : resizeBottom;
            resizeSecondary = xIsMain ? resizeBottom : resizeLeft;
          } else if (topRight) {
            resizeMain = xIsMain ? resizeRight : resizeTop;
            resizeSecondary = xIsMain ? resizeTop : resizeRight;
          } else {
            resizeMain = xIsMain ? resizeLeft : resizeTop;
            resizeSecondary = xIsMain ? resizeTop : resizeLeft;
          }
          resizeMain();
          resizeSecondary();
        } else if (resize.top) {
          resizeTop();
        } else if (resize.right) {
          resizeRight();
        } else if (resize.bottom) {
          resizeBottom();
        } else if (resize.left) {
          resizeLeft();
        }
        selectionCart.markManualResize();
      }, 40),
    ),
    on(window, 'pointerup', () => {
      // cleanup
      tearDown(eventKeys);
      relativeContainer.classList.remove('disable-user-selection');
      selectionCart.classList.remove('is-resizing');
      // add the width and height when done
      selectionCart.style.width = px(selectionCart.clientWidth);
      selectionCart.style.height = px(selectionCart.clientHeight);
      selectionCart.storeLastPosition();
    }),
  ];
}
